/**
 * Guarded localStorage access.
 *
 * Mobile Safari throws `SecurityError: The operation is insecure.` on ANY
 * localStorage access — including reads — when storage is unavailable (private
 * browsing, "Block All Cookies", or ITP partitioning). Some browsers also throw
 * on writes when the quota is exhausted. An unguarded access in a render path
 * (e.g. a `useState` initializer) propagates the throw out of React's render and
 * takes down the whole app.
 *
 * These helpers swallow such failures and fall back to a safe default, so
 * "storage unavailable" degrades to "no persisted value" instead of a crash.
 * See Sentry CHAT-MARITACA-26.
 */
function getLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    // Even reading the `localStorage` property can throw in sandboxed iframes.
    return null;
  }
}

export const safeStorage = {
  getItem(key: string): string | null {
    try {
      return getLocalStorage()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      getLocalStorage()?.setItem(key, value);
    } catch {
      /* storage unavailable or over quota — ignore */
    }
  },
  removeItem(key: string): void {
    try {
      getLocalStorage()?.removeItem(key);
    } catch {
      /* storage unavailable — ignore */
    }
  },
};
